import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useScrapeBatchStatus } from '@/hooks/useScrapeBatchStatus';

const getStatusBadge = (status) => {
  const badges = {
    queued: { variant: 'outline', icon: '⏳', text: 'Queued', className: 'border-yellow-500 text-yellow-700' },
    in_progress: { variant: 'outline', icon: '🔄', text: 'In Progress', className: 'border-blue-500 text-blue-700' },
    completed: { variant: 'outline', icon: '✅', text: 'Completed', className: 'border-green-500 text-green-700' },
    failed: { variant: 'outline', icon: '❌', text: 'Failed', className: 'border-red-500 text-red-700' },
    cancelled: { variant: 'outline', icon: '🚫', text: 'Cancelled', className: 'border-gray-500 text-gray-700' }
  };

  const config = badges[status] || badges.queued;
  
  return (
    <Badge variant={config.variant} className={config.className}>
      <span className="mr-1">{config.icon}</span>
      {config.text}
    </Badge>
  );
};

const ScrapeTimeline = ({ clientId, onComplete }) => {
  const { batch, tasks, summary, isLoading, error, clearBatch, hasBatch, cancelBatch, isCancelling } = useScrapeBatchStatus(clientId, { onComplete });

  if (!hasBatch || !batch) {
    return null;
  }

  const isActive = batch.is_active;
  const completedCount = summary?.completed || 0;
  const totalCount = summary?.total || 0;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleDismiss = () => {
    clearBatch();
  };

  const handleCancel = async () => {
    await cancelBatch();
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              {isActive ? 'Scraping URLs...' : 'Scraping Complete'}
            </CardTitle>
            <CardDescription>
              {isActive 
                ? `Processing ${completedCount} of ${totalCount} URLs`
                : `Processed ${totalCount} URL${totalCount !== 1 ? 's' : ''}`
              }
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                {isCancelling ? 'Stopping...' : 'Stop Scraping'}
              </Button>
            )}
            {!isActive && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{completedCount} completed</span>
            {summary?.failed > 0 && (
              <span className="text-red-600">{summary.failed} failed</span>
            )}
            {summary?.cancelled > 0 && (
              <span className="text-gray-600">{summary.cancelled} cancelled</span>
            )}
            {summary?.in_progress > 0 && (
              <span className="text-blue-600">{summary.in_progress} in progress</span>
            )}
            {summary?.queued > 0 && (
              <span className="text-yellow-600">{summary.queued} queued</span>
            )}
          </div>
        </div>

        {tasks.length > 0 && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
              >
                <div className="flex-1 min-w-0 mr-3">
                  <p className="text-sm font-medium truncate" title={task.url}>
                    {task.url}
                  </p>
                  {task.error_message && (
                    <p className="text-xs text-red-600 mt-1 truncate" title={task.error_message}>
                      {task.error_message}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(task.status)}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 p-3 rounded-lg bg-red-50 border border-red-200">
            {error}
          </div>
        )}

        {!isActive && (
          <div className="flex justify-end">
            <Button onClick={handleDismiss} variant="outline" size="sm">
              Dismiss
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScrapeTimeline;
